// Columna central del explorer (`.col-table` del mockup): barra de pills por estado, contador +
// paginación, y la tabla compacta de OCHO columnas.
//
// Las ocho son las del mockup, en su orden: ID · Tipo · Venta · Solicitante · Tienda · Impacto ·
// Solicitada · Estado. Antes había cinco, y tres de las que faltaban —quién la pidió, de qué venta
// y de qué tienda— son justo por las que se navega una bandeja de excepciones.
//
// Los pills muestran el universo COMPLETO (los cuenta el silo, no la vista filtrada): son el mapa
// de la bandeja, no un resumen de lo que quedó a la vista.
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import { ErrorState, Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateMx } from '@/lib/dateHelpers';
import type {
    ExceptionRow,
    ExceptionStatus,
    ExceptionStatusCounts,
} from '@/api/services/retail-wizard/backoffice';
import {
    caseSummary,
    formatImpactShort,
    nextSort,
    PAGE_SIZE,
    pluralExceptions,
    requesterOf,
    statusBadge,
    typeBadge,
    type SortKey,
    type SortState,
} from '../lib/excepcionesFormat';

const PILLS: {
    key: ExceptionStatus | null;
    label: string;
    countKey: keyof ExceptionStatusCounts;
    valueClass: string;
}[] = [
    { key: null, label: 'Todas', countKey: 'all', valueClass: 'text-foreground' },
    { key: 'NEW', label: 'Nuevas', countKey: 'NEW', valueClass: 'text-destructive' },
    { key: 'IN_REVIEW', label: 'En revisión', countKey: 'IN_REVIEW', valueClass: 'text-warning' },
    { key: 'APPROVED', label: 'Aprobadas', countKey: 'APPROVED', valueClass: 'text-success' },
    { key: 'APPLIED', label: 'Aplicadas', countKey: 'APPLIED', valueClass: 'text-info' },
    {
        key: 'REJECTED',
        label: 'Rechazadas',
        countKey: 'REJECTED',
        valueClass: 'text-muted-foreground',
    },
];

/**
 * Anchos del mockup, con dos correcciones obligadas por el dato real:
 *   · ID — el mockup muestra `EXC-001`; el silo devuelve un ULID de 26 caracteres. Se le dan 116px
 *     y se trunca con el valor completo en el `title`, en vez de fingir un folio corto que no hay.
 *   · Solicitada — `DD/MMM/YYYY` son 11 caracteres contra los 8 del `DD/MM/YY` del mockup.
 */
const COLUMNS: { key: SortKey; label: string; width: string; numeric?: boolean }[] = [
    { key: 'exceptionId', label: 'ID', width: 'w-[116px]' },
    { key: 'type', label: 'Tipo', width: 'w-[116px]' },
    { key: 'saleId', label: 'Venta', width: 'w-[104px]' },
    { key: 'requestedBy', label: 'Solicitante', width: 'w-[124px]' },
    { key: 'storeId', label: 'Tienda', width: 'w-[124px]' },
    { key: 'impact', label: 'Impacto', width: 'w-[80px]', numeric: true },
    { key: 'requestedAt', label: 'Solicitada', width: 'w-[92px]' },
    { key: 'status', label: 'Estado', width: 'w-[84px]' },
];

/** La suma de los anchos fijos. Debajo de esto, `table-fixed` empieza a pisar los encabezados. */
const MIN_TABLE_WIDTH = 'min-w-[840px]';

const TD = 'truncate border-b border-border/60 px-1.5 py-1 text-[11.5px]';

export function ExcepcionesTable({
    rows,
    counts,
    activePill,
    onPillChange,
    sort,
    onSortChange,
    page,
    onPageChange,
    selectedId,
    onSelect,
    storeNameOf,
    userNameOf,
    isLoading,
    error,
    onRetry,
}: {
    /** Filas YA filtradas y ordenadas — la paginación se aplica aquí adentro. */
    rows: ExceptionRow[];
    counts?: ExceptionStatusCounts;
    activePill: ExceptionStatus | null;
    onPillChange: (status: ExceptionStatus | null) => void;
    sort: SortState;
    onSortChange: (next: SortState) => void;
    page: number;
    onPageChange: (page: number) => void;
    selectedId: string | null;
    onSelect: (id: string) => void;
    storeNameOf: (storeId: string | null) => string | null;
    userNameOf: (userId: string | null) => string | null;
    isLoading: boolean;
    error: Error | null;
    onRetry: () => void;
}) {
    // La lectura caída corta TODO: los pills viven fuera de la tabla y sin este corte temprano
    // quedan seis contadores encima del error afirmando un universo que no se pudo leer.
    if (error) {
        return (
            <ErrorState
                className="m-3"
                title="No se pudieron cargar las excepciones"
                description={error.message}
                onRetry={onRetry}
            />
        );
    }

    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const pageRows = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    return (
        <>
            {/* .stats-bar */}
            <div className="flex min-h-10 flex-none items-center gap-1 border-b border-border px-2 py-1.5">
                {PILLS.map((pill) => {
                    const on = pill.key === activePill;
                    return (
                        <button
                            key={pill.label}
                            type="button"
                            aria-pressed={on}
                            onClick={() => {
                                onPillChange(pill.key);
                            }}
                            className={cn(
                                'flex min-w-0 flex-1 items-baseline justify-center gap-[5px] whitespace-nowrap rounded-[5px] border px-2 py-1 transition-colors',
                                on
                                    ? 'border-primary bg-accent'
                                    : 'border-border bg-card hover:bg-accent/50',
                            )}
                        >
                            <span
                                className={cn(
                                    'text-[9.5px] font-bold uppercase tracking-[0.05em]',
                                    on ? 'text-foreground' : 'text-muted-foreground',
                                )}
                            >
                                {pill.label}
                            </span>
                            {/* Con la lectura en vuelo se pinta «—» y no «0»: cero es una afirmación
                                sobre los datos, y acá todavía no hay datos. */}
                            <span
                                className={cn(
                                    'text-[12px] font-bold tabular-nums',
                                    counts ? pill.valueClass : 'text-muted-foreground',
                                )}
                            >
                                {counts ? counts[pill.countKey] : '—'}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* .table-toolbar */}
            <div className="flex flex-none flex-wrap items-center gap-2 border-b border-border px-3 py-2">
                <div className="text-[11px] text-muted-foreground">
                    {isLoading ? '— excepciones' : pluralExceptions(rows.length)}
                </div>
                <div className="flex-1" />
                <div className="flex items-center gap-1 text-[11px]">
                    <button
                        type="button"
                        aria-label="Página anterior"
                        disabled={safePage <= 1}
                        onClick={() => {
                            onPageChange(safePage - 1);
                        }}
                        className="flex size-[22px] items-center justify-center rounded border border-border bg-card transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <ChevronLeft className="size-3" aria-hidden />
                    </button>
                    <span className="tabular-nums">
                        {safePage} / {totalPages}
                    </span>
                    <button
                        type="button"
                        aria-label="Página siguiente"
                        disabled={safePage >= totalPages}
                        onClick={() => {
                            onPageChange(safePage + 1);
                        }}
                        className="flex size-[22px] items-center justify-center rounded border border-border bg-card transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <ChevronRight className="size-3" aria-hidden />
                    </button>
                </div>
            </div>

            {/* `overflow-auto` y no solo `-y`: con ocho columnas de ancho fijo la tabla no entra en
                la columna central a 1280px, y antes que colapsar una columna se scrollea. */}
            <div className="min-h-0 flex-1 overflow-auto">
                <table role="grid" className={cn('w-full table-fixed', MIN_TABLE_WIDTH)}>
                    <thead className="sticky top-0 z-10 bg-card">
                        <tr className="border-b border-border">
                            {COLUMNS.map((col) => {
                                const active = sort.key === col.key;
                                return (
                                    <th
                                        key={col.key}
                                        scope="col"
                                        aria-sort={
                                            active
                                                ? sort.direction === 'asc'
                                                    ? 'ascending'
                                                    : 'descending'
                                                : 'none'
                                        }
                                        className={cn(
                                            'px-1.5 py-1 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground',
                                            col.width,
                                            col.numeric ? 'text-right' : 'text-left',
                                        )}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => {
                                                onSortChange(nextSort(sort, col.key));
                                            }}
                                            className={cn(
                                                'inline-flex select-none items-center gap-0.5 transition-colors hover:text-foreground',
                                                active && 'text-foreground',
                                            )}
                                        >
                                            {col.label}
                                            {active ? (
                                                sort.direction === 'asc' ? (
                                                    <ChevronUp className="size-2.5" aria-hidden />
                                                ) : (
                                                    <ChevronDown className="size-2.5" aria-hidden />
                                                )
                                            ) : null}
                                        </button>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading
                            ? Array.from({ length: 8 }, (_u, i) => (
                                  <tr key={`skeleton-${String(i)}`}>
                                      {COLUMNS.map((col) => (
                                          <td key={col.key} className={TD}>
                                              <Skeleton className="h-3" />
                                          </td>
                                      ))}
                                  </tr>
                              ))
                            : pageRows.map((row) => {
                                  const selected = row.exceptionId === selectedId;
                                  const type = typeBadge(row.type);
                                  const status = statusBadge(row.status);
                                  const storeName = storeNameOf(row.storeId);
                                  const requester = requesterOf(row, userNameOf);
                                  return (
                                      <tr
                                          key={row.exceptionId}
                                          role="row"
                                          // 🔴 Las filas tenían `onClick` y nada más: cero elementos
                                          // focusables, así que con teclado NO se podía seleccionar
                                          // ninguna excepción — el Tab saltaba del buscador a los
                                          // botones del caso que la pantalla había elegido sola.
                                          tabIndex={0}
                                          // El mockup no tiene columna de título; el texto del caso
                                          // vive en la ficha. Acá va como tooltip para que la fila
                                          // siga diciendo de qué se trata sin robarle ancho a las
                                          // ocho columnas del diseño.
                                          title={caseSummary(row)}
                                          onClick={() => {
                                              onSelect(row.exceptionId);
                                          }}
                                          onKeyDown={(e) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                  e.preventDefault();
                                                  onSelect(row.exceptionId);
                                              }
                                          }}
                                          aria-selected={selected}
                                          className={cn(
                                              'cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                              selected
                                                  ? 'bg-success-soft/55 shadow-[inset_3px_0_0_hsl(var(--success))]'
                                                  : 'hover:bg-accent/40',
                                          )}
                                      >
                                          <td className={cn(TD, 'font-mono text-[10.5px]')}>
                                              {/* eslint-disable-next-line no-restricted-syntax -- el identificador del caso, el que se cita en la bitácora de auditoría */}
                                              {row.exceptionId}
                                          </td>
                                          <td className={cn(TD, 'overflow-visible')}>
                                              <span
                                                  className={cn(
                                                      'inline-block rounded px-1.5 py-px text-[9.5px] font-bold',
                                                      type.className,
                                                  )}
                                              >
                                                  {type.label}
                                              </span>
                                          </td>
                                          <td className={cn(TD, 'font-mono text-[10.5px]')}>
                                              {row.saleId ?? '—'}
                                          </td>
                                          <td className={TD}>{requester}</td>
                                          <td className={TD}>
                                              {storeName ?? row.storeId ?? 'Sin tienda'}
                                          </td>
                                          <td
                                              className={cn(
                                                  TD,
                                                  'text-right font-semibold tabular-nums',
                                                  row.impactCents !== null &&
                                                      row.impactCents < 0 &&
                                                      'text-success',
                                                  row.impactCents === null &&
                                                      'text-muted-foreground',
                                              )}
                                          >
                                              {formatImpactShort(row.impactCents)}
                                          </td>
                                          {/* 🔴 Era `detectedAt`, un campo que el silo no manda:
                                              `dayjs(undefined)` devuelve AHORA, así que las 213
                                              filas pintaban la fecha de hoy. */}
                                          <td className={cn(TD, 'tabular-nums')}>
                                              {formatDateMx(row.requestedAt)}
                                          </td>
                                          <td className={cn(TD, 'overflow-visible')}>
                                              <span
                                                  className={cn(
                                                      'inline-block rounded-full border px-1.5 py-px text-[9.5px] font-semibold',
                                                      status.className,
                                                  )}
                                              >
                                                  {status.label}
                                              </span>
                                          </td>
                                      </tr>
                                  );
                              })}
                    </tbody>
                </table>
                {!isLoading && rows.length === 0 ? (
                    <div className="py-8 text-center text-[12px] text-muted-foreground">
                        Ninguna excepción coincide con los filtros.
                    </div>
                ) : null}
            </div>
        </>
    );
}
